import type {
  ActionResult,
  FieldAction,
  FieldValueAction,
  FieldPropertiesAction,
} from '../logic-builder-types';
import type { ActionType } from '../constants';

import {
  ACTION_TYPE_SHOW_HIDE,
  ACTION_TYPE_ENABLE_DISABLE,
  ACTION_TYPE_SET_VALUE,
  ALLOWED_SET_PROPERTIES,
  ACTION_TYPE_SET_PROPERTY,
} from '../constants';

function executeShowHideAction(
  actionType: ActionType,
  data: FieldAction,
): ActionResult {
  const { fieldId, action } = data;
  const isVisible = action === 'show';
  const newState = {
    visible: isVisible,
    required: isVisible ? undefined : false,
  } as const;

  return { fieldId, newState };
}

function executeEnableDisableAction(
  actionType: ActionType,
  data: FieldAction,
): ActionResult {
  const { fieldId, action } = data;
  const isDisabled = action === 'disable';
  const newState = {
    disabled: isDisabled,
    required: isDisabled ? false : undefined,
  } as const;
  return { fieldId, newState };
}

function executeSetValueAction(data: FieldValueAction): ActionResult {
  const { fieldId } = data;
  const newState = { value: data.value } as const;

  return { fieldId, newState };
}

function executeSetPropertyAction(data: FieldPropertiesAction): ActionResult {
  const { fieldId } = data;
  let newState = { ...data.properties };

  newState = Object.keys(newState)
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
    .filter(key => ALLOWED_SET_PROPERTIES.includes(key))
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    .reduce<Record<string, any>>((obj, key) => {
      obj[key] = newState[key];
      return obj;
    }, {});

  return { fieldId, newState };
}

function executeAction(
  actionType: ActionType,
  actionData:
    | Array<FieldAction>
    | Array<FieldValueAction>
    | Array<FieldPropertiesAction>,
): Array<ActionResult> {
  const results: Array<ActionResult> = [];

  for (const data of actionData) {
    if (!data.fieldId) {
      throw new Error(
        `Missing fieldId for action: ${actionType} in action data: ${JSON.stringify(
          data,
        )}`,
      );
    }

    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    let result: Record<string, any> = {};

    switch (actionType) {
      case ACTION_TYPE_SHOW_HIDE:
        result = executeShowHideAction(actionType, data as FieldAction);

        break;

      case ACTION_TYPE_ENABLE_DISABLE:
        result = executeEnableDisableAction(actionType, data as FieldAction);

        break;

      case ACTION_TYPE_SET_VALUE:
        result = executeSetValueAction(data as FieldValueAction);

        break;

      case ACTION_TYPE_SET_PROPERTY:
        result = executeSetPropertyAction(data as FieldPropertiesAction);

        break;
      default:
        throw new Error(`Unknown action type: ${actionType}`);
    }

    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
    results.push(result);
  }

  return results;
}

export { executeAction };
